import React, { useState, useEffect } from 'react';
import PlayerStats from './PlayerStats.js';
import Field from './Field.js';
import Hand from './Hand.js';
import NumberInput from './NumberInput.js';
import CardDetail from './CardDetail.js';
import CardActionMenu from './CardActionMenu.js';
import GameInfo from './GameInfo.js';
import GameLogOverlay from './GameLogOverlay.js';
import NPCController from './NPCController.js';
import RulesOverlay from './overlays/RulesOverlay.js';
import MulliganModal from './MulliganModal.js'; // マリガンモーダルをインポート
import TutorialModal from './overlays/TutorialModal.js'; // チュートリアルモーダル
import TutorialHighlight from './TutorialHighlight.js'; // チュートリアルハイライト
import useAnimationManager from '../hooks/useAnimationManager.js';
import usePresentedCards from '../hooks/usePresentedCards.js'; // 新しいフックをインポート
import useTutorialController from '../hooks/useTutorialController.js'; // チュートリアルフック
import useWindowSize from '../hooks/useWindowSize.js'; // ウィンドウサイズフックをインポート
import { getCardName, getCardTypeName, getUILoc } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import '../App.css';
import { HUMAN_PLAYER_ID, NPC_PLAYER_ID, GamePhase } from '../gameLogic/constants.js'; // GamePhaseをインポート
import { produce } from 'immer'; // immerをインポート
import audioManager from '../utils/AudioManager.js';

const Game = ({ gameState, cardDefs, onPlayCard, onEndTurn, onProvideInput, onSurrender, onGameStateUpdate, effectMonitor, enhancedLog, onConfirmMulligan, onRedevelopmentPlan }) => {
    const [selectedCard, setSelectedCard] = useState(null);
    const [actionMenuCard, setActionMenuCard] = useState(null);
    const [showGameLog, setShowGameLog] = useState(false);
    const [showRulesOverlay, setShowRulesOverlay] = useState(false);
    const [showSurrenderConfirm, setShowSurrenderConfirm] = useState(false);
    const [showRedevelopmentModal, setShowRedevelopmentModal] = useState(false);
    const [redevelopmentConfirmCard, setRedevelopmentConfirmCard] = useState(null);
    const [isClosingPrompt, setIsClosingPrompt] = useState(false);
    const { width } = useWindowSize();
    const isWidescreen = width > 1024; // 1024px以上をワイドスクリーンと判定
    
    // インゲームBGMの初期化と管理
    useEffect(() => {
        // 対局用BGMトラックを開始
        // ファイル名は想定：game_base.wav, game_player.wav, game_npc.wav
        const bgmPack = gameState?.bgmPack || 'game'; // デフォルトは 'game'
        audioManager.startInGameBGM(`${bgmPack}_base`, `${bgmPack}_player`, `${bgmPack}_npc`);
        
        return () => {
            // ゲーム画面を離れる際に停止
            audioManager.stopBGM();
        };
    }, [gameState?.bgmPack]);

    // イデオロギー状況によるBGMフェード制御
    useEffect(() => {
        if (gameState && gameState.players) {
            const hasPlayerIdeology = !!gameState.players[HUMAN_PLAYER_ID]?.ideology;
            const hasNPCIdeology = !!gameState.players[NPC_PLAYER_ID]?.ideology;
            
            console.log(`[BGM Control] Player Ideology: ${hasPlayerIdeology}, NPC Ideology: ${hasNPCIdeology}`);
            audioManager.updateInGameBGM(hasPlayerIdeology, hasNPCIdeology);
        }
    }, [gameState]);

    // チュートリアル・コントローラーの初期化
    const tutorialData = gameState?.tutorial;
    const {
        activeStep,
        isModalOpen,
        activeHighlight,
        handleModalCloseAction,
        wrappedOnPlayCard,
        wrappedOnEndTurn,
        wrappedOnCardClick,
        wrappedOnHighlightClick,
        wrappedOnProvideInput
    } = useTutorialController(
        tutorialData?.master || null,
        gameState,
        onPlayCard,
        onEndTurn,
        onProvideInput
    );

    // チュートリアル中はラップされた関数を使用
    const effectiveOnPlayCard = tutorialData ? wrappedOnPlayCard : onPlayCard;
    const effectiveOnEndTurn = tutorialData ? wrappedOnEndTurn : onEndTurn;
    const effectiveOnProvideInput = tutorialData ? wrappedOnProvideInput : onProvideInput;

    // 選択肢（モーダルなど）からの入力処理
    const handleChoiceInput = (input) => {
        audioManager.playSE('draw'); // 選択肢からの決定（プレイ）時にもドロー音
        setIsClosingPrompt(true);
        // アニメーション完了（約200-300ms）を待ってから実際の入力を送信
        setTimeout(() => {
            effectiveOnProvideInput(input);
            setIsClosingPrompt(false);
        }, 250);
    };

    // サレンダーキャンセルの待機処理
    const handleCancelSurrender = () => {
        audioManager.playSE('cancel');
        setIsClosingPrompt(true);
        setTimeout(() => {
            setShowSurrenderConfirm(false);
            setIsClosingPrompt(false);
        }, 250);
    };


    const humanPlayer = gameState?.players?.[HUMAN_PLAYER_ID];
    const npcPlayer = gameState?.players?.[NPC_PLAYER_ID];
    
    // 新しい演出システムを初期化
    const {
        controller: presentationController,
        handleEffectForAnimation,
        isReady: animationReady
    } = useAnimationManager(gameState, effectMonitor);

    // プレゼンテーションロジック用のカード状態を管理
    const { presentedCards: humanPresentedCards, handleAnimationEnd: handleHumanAnimationEnd } = usePresentedCards(humanPlayer?.field, humanPlayer?.ideology);
    const { presentedCards: npcPresentedCards, handleAnimationEnd: handleNpcAnimationEnd } = usePresentedCards(npcPlayer?.field, npcPlayer?.ideology);

    // EffectMonitorと演出システムの連携
    React.useEffect(() => {
        const effectMonitor = enhancedLog.getEffectMonitor();
        
        if (effectMonitor && animationReady && handleEffectForAnimation) {
            
            
            effectMonitor.registerAnimationCallback(handleEffectForAnimation);
            
            
            // presentationControllerをgameStateに設定（effectHandlerで使用）
            // gameState.presentationController = presentationController;
            
            // presentationControllerにgameStateへの参照を設定
            if (presentationController && presentationController.setGameState) {
                presentationController.setGameState(gameState);
            } else {
            }
            
            
            return () => {
                
                effectMonitor.removeAnimationCallback(handleEffectForAnimation);
            };
        } else {
            
            
            
            
            
        }
    }, [enhancedLog, animationReady, handleEffectForAnimation, presentationController, gameState]); // gameStateを依存配列に追加


    // 継続演出の更新（規模や手札の変更を監視）
    React.useEffect(() => {
        if (gameState && presentationController && presentationController.animationManager) {
            console.log('[DEBUG] Game.js: Calling updatePersistentAnimations due to gameState change.'); // Add this log
            presentationController.animationManager.updatePersistentAnimations(gameState);
        }
    }, [
        gameState, // gameState全体を監視対象とする
        presentationController
    ]);

  useEffect(() => {
    const processAnimation = async () => {
      if (gameState?.animation_queue?.length > 0 && handleEffectForAnimation && !gameState.isAnimationLocked && animationReady) { // Add animationReady to condition
        const nextAnimation = gameState.animation_queue[0];
        const effect = nextAnimation.effect;
        const sourceCard = nextAnimation.sourceCard;

        
        
        // アニメーションをトリガーし、完了を待つ
        await handleEffectForAnimation(effect, sourceCard);
        
        // 完了後にのみ、処理されたアニメーションをキューから削除
        onGameStateUpdate(currentGameState => produce(currentGameState, draft => {
          
          draft.animation_queue.shift();
        }));
      } else {
        
        
        
        
        
      }
    };

    processAnimation();
  }, [gameState, onGameStateUpdate, handleEffectForAnimation, animationReady]); // Add animationReady to dependencies

    if (!gameState) {
        return <div>Loading Game...</div>;
    }

    const { current_turn, awaiting_input } = gameState;
    
    // スクロール対応なのでmaxFieldSizeは不要（Fieldコンポーネント内で動的に管理）

    // カードクリック処理
    const handleCardClick = (card) => {
        // チュートリアル用のクリック検知
        if (tutorialData) {
            wrappedOnCardClick(card);
        }

        // 選択待ち状態では詳細表示のみ許可（選択はオーバーレイから行う）
        if (awaiting_input && (awaiting_input.type === 'CHOICE_CARD_FOR_EFFECT' || awaiting_input.type === 'CHOICE_CARDS_FOR_OPERATION')) {
            // 選択はオーバーレイから行うため、ここでは詳細表示のみ
            setSelectedCard(card);
            return;
        }
        
        // 通常時の処理
        if (humanPlayer.hand.includes(card)) {
            // 手札のカードの場合
            if (!awaiting_input) {
                // 通常時はアクションメニューを表示
                setActionMenuCard(card);
            } else {
                // その他の選択待ち状態では詳細表示のみ
                setSelectedCard(card);
            }
        } else {
            // その他のカードは詳細表示
            setSelectedCard(card);
        }
    };

    const handleSurrenderClick = () => {
        setShowSurrenderConfirm(true);
    };

    // 再開発計画ボタンのクリック処理
    const handleRedevelopmentClick = () => {
        audioManager.playSE('select');
        setShowRedevelopmentModal(true);
    };

    // 再開発計画モーダルのキャンセル処理
    const handleCancelRedevelopment = () => {
        audioManager.playSE('cancel');
        setIsClosingPrompt(true);
        setTimeout(() => {
            setShowRedevelopmentModal(false);
            setRedevelopmentConfirmCard(null);
            setIsClosingPrompt(false);
        }, 250);
    };

    // 再開発計画：カード選択後の確認処理
    const handleRedevelopmentCardSelect = (card) => {
        audioManager.playSE('select');
        setRedevelopmentConfirmCard(card);
    };

    // 再開発計画：除外確定処理
    const handleRedevelopmentConfirm = () => {
        if (!redevelopmentConfirmCard) return;
        audioManager.playSE('damage');
        setIsClosingPrompt(true);
        setTimeout(() => {
            onRedevelopmentPlan(HUMAN_PLAYER_ID, redevelopmentConfirmCard.instance_id);
            setShowRedevelopmentModal(false);
            setRedevelopmentConfirmCard(null);
            setIsClosingPrompt(false);
        }, 250);
    };

    // 再開発計画ボタンが使用可能かどうかを判定
    const canUseRedevelopmentPlan = () => {
        if (!humanPlayer || current_turn !== HUMAN_PLAYER_ID) return false;
        if (humanPlayer.redevelopment_used) return false;
        const fieldWealthCount = humanPlayer.field.filter(c => c.card_type === 1).length;
        return fieldWealthCount > Math.floor(humanPlayer.field_limit / 2);
    };

    // アクションメニューからのプレイ処理
    const handlePlayFromMenu = () => {
        if (actionMenuCard) {
            effectiveOnPlayCard(actionMenuCard); // ラップされた方を使用
            setActionMenuCard(null);
        }
    };

    // アクションメニューからの詳細表示処理は不要（統合されたため）

    const renderChoicePrompt = () => {
        if (!awaiting_input || awaiting_input.player_id !== current_turn) {
            return null;
        }

        // NPCのターンの時は選択オーバーレイを表示しない（相手の戦略が見えてしまうため）
        if (awaiting_input.player_id !== HUMAN_PLAYER_ID) {
            return null;
        }

        if (awaiting_input.type === 'CHOICE_NUMBER') {
            return (
                <NumberInput 
                    prompt={awaiting_input.prompt}
                    min={awaiting_input.min}
                    max={awaiting_input.max}
                    onConfirm={handleChoiceInput}
                    isClosing={isClosingPrompt}
                />
            );
        }

        if (awaiting_input.type === 'CHOICE_CARD_TO_ADD') {
            return (
                <div className={`choice-prompt-overlay ${isClosingPrompt ? 'closing' : ''}`}>
                    <div id="choice-prompt-container" className={`choice-prompt ${isClosingPrompt ? 'closing' : ''}`}>
                        <h3>{getUILoc('MESSAGES.SELECT_CARD') || 'カードを選択してください'}</h3>
                        <div className="choice-options-scrollable">
                            {awaiting_input.options.map(option => {
                                const cardDef = cardDefs[option];
                                if (!cardDef) return null;
                                return (
                                    <div key={option} className="choice-card-container">
                                        <button className="choice-card-select" onClick={() => handleChoiceInput(option)}>
                                            <div className="choice-card-illustration">
                                                <CardIllustration cardId={option} />
                                            </div>
                                            <div className="choice-card-info">
                                                <span className="choice-card-name">{getCardName(option)}</span>
                                                <span className="choice-card-type">({getCardTypeName(cardDef.card_type)})</span>
                                                {cardDef.durability !== undefined && (
                                                    <span className="choice-card-durability">{getUILoc('LABELS.DURABILITY') || '耐久'}{cardDef.durability}</span>
                                                )}
                                                {cardDef.required_scale && (
                                                    <span className="choice-card-scale">{getUILoc('LABELS.REQUIRED_SCALE') || '必要規模'}{cardDef.required_scale}</span>
                                                )}
                                            </div>
                                        </button>
                                        <button
                                            className="choice-card-detail"
                                            onClick={() => setSelectedCard(cardDef)}
                                            title={getUILoc('BUTTONS.DETAIL') || '詳細を見る'}
                                            disabled={!!(tutorialData && activeHighlight)}
                                        >
                                            📋
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }

        if (awaiting_input.type === 'CHOICE_CARD_FROM_PILE') {
            return (
                <div className={`choice-prompt-overlay ${isClosingPrompt ? 'closing' : ''}`}>
                    <div id="choice-prompt-container" className={`choice-prompt ${isClosingPrompt ? 'closing' : ''}`}>
                        <h3>{getUILoc('MESSAGES.SELECT_ONE_CARD') || 'カードを1枚選択してください'}</h3>
                        <div className="choice-options-scrollable">
                            {awaiting_input.options.map(card => (
                                <div key={card.instance_id} className="choice-card-container">
                                    <button className="choice-card-select" onClick={() => handleChoiceInput(card)}>
                                        <div className="choice-card-illustration">
                                            <CardIllustration cardId={card.id} />
                                        </div>
                                        <div className="choice-card-info">
                                            <span className="choice-card-name">{getCardName(card.id)}</span>
                                            <span className="choice-card-type">({getCardTypeName(card.card_type)})</span>
                                            {card.current_durability !== undefined && (
                                                <span className="choice-card-durability">{getUILoc('LABELS.DURABILITY') || '耐久'}{card.current_durability}</span>
                                            )}
                                            {card.required_scale && (
                                                <span className="choice-card-scale">{getUILoc('LABELS.REQUIRED_SCALE') || '必要規模'}{card.required_scale}</span>
                                            )}
                                        </div>
                                    </button>
                                    <button
                                        className="choice-card-detail"
                                        onClick={() => setSelectedCard(card)}
                                        title={getUILoc('BUTTONS.DETAIL') || '詳細を見る'}
                                        disabled={!!(tutorialData && activeHighlight)}
                                    >
                                        📋
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (awaiting_input.type === 'CHOICE_CARDS_FOR_OPERATION') {
            const sourceCardName = awaiting_input.source_card_name || '不明なカード';
            return (
                <div className={`choice-prompt-overlay ${isClosingPrompt ? 'closing' : ''}`}>
                    <div id="choice-prompt-container" className={`choice-prompt ${isClosingPrompt ? 'closing' : ''}`}>
                        <h3>{sourceCardName}の効果</h3>
                        <p>{getUILoc('MESSAGES.SELECT_WEALTH_FROM_HAND') || '手札から財カードを1枚選択してください'}</p>
                        <div className="choice-options-scrollable">
                            {awaiting_input.options.map(card => (
                                <div key={card.instance_id} className="choice-card-container">
                                    <button className="choice-card-select" onClick={() => handleChoiceInput([card])}>
                                        <div className="choice-card-illustration">
                                            <CardIllustration cardId={card.id} />
                                        </div>
                                        <div className="choice-card-info">
                                            <span className="choice-card-name">{getCardName(card.id)}</span>
                                            <span className="choice-card-type">({getCardTypeName(card.card_type)})</span>
                                            {card.current_durability !== undefined && (
                                                <span className="choice-card-durability">{getUILoc('LABELS.DURABILITY') || '耐久'}{card.current_durability}</span>
                                            )}
                                            {card.required_scale && (
                                                <span className="choice-card-scale">{getUILoc('LABELS.REQUIRED_SCALE') || '必要規模'}{card.required_scale}</span>
                                            )}
                                        </div>
                                    </button>
                                    <button
                                        className="choice-card-detail"
                                        onClick={() => setSelectedCard(card)}
                                        title={getUILoc('BUTTONS.DETAIL') || '詳細を見る'}
                                        disabled={!!(tutorialData && activeHighlight)}
                                    >
                                        📋
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (awaiting_input.type === 'CHOICE_CARD_FOR_EFFECT') {
            const sourceCardName = awaiting_input.source_card_name || '不明なカード';
            return (
                <div className={`choice-prompt-overlay ${isClosingPrompt ? 'closing' : ''}`}>
                    <div id="choice-prompt-container" className={`choice-prompt ${isClosingPrompt ? 'closing' : ''}`}>
                        <h3>{sourceCardName}の効果</h3>
                        <p>{awaiting_input.prompt || (getUILoc('MESSAGES.SELECT_ONE_CARD') || 'カードを1枚選択してください')}</p>
                        <div className="choice-options-scrollable">
                            {awaiting_input.options.map(card => (
                                <div key={card.instance_id} className="choice-card-container">
                                    <button className="choice-card-select" onClick={() => handleChoiceInput(card)}>
                                        <div className="choice-card-illustration">
                                            <CardIllustration cardId={card.id} />
                                        </div>
                                        <div className="choice-card-info">
                                            <span className="choice-card-name">{getCardName(card.id)}</span>
                                            <span className="choice-card-type">({getCardTypeName(card.card_type)})</span>
                                            {card.current_durability !== undefined && (
                                                <span className="choice-card-durability">{getUILoc('LABELS.DURABILITY') || '耐久'}{card.current_durability}</span>
                                            )}
                                            {card.required_scale && (
                                                <span className="choice-card-scale">{getUILoc('LABELS.REQUIRED_SCALE') || '必要規模'}{card.required_scale}</span>
                                            )}
                                        </div>
                                    </button>
                                    <button
                                        className="choice-card-detail"
                                        onClick={() => setSelectedCard(card)}
                                        title={getUILoc('BUTTONS.DETAIL') || '詳細を見る'}
                                        disabled={!!(tutorialData && activeHighlight)}
                                    >
                                        📋
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        // For other types of choices, like selecting a card from hand/field
        if (awaiting_input.prompt) {
             return <div className={`prompt-overlay ${isClosingPrompt ? 'closing' : ''}`}><div className={`prompt ${isClosingPrompt ? 'closing' : ''}`}>{awaiting_input.prompt}</div></div>;
        }

        return null;
    };



    return (
        <div className="game-board">
            {/* チュートリアルハイライト表示用オーバーレイ */}
            {activeHighlight && (
                <TutorialHighlight 
                    targetId={activeHighlight.targetId} 
                    onTargetClick={activeStep?.completionCondition?.type === 'CLICK_TARGET' ? wrappedOnHighlightClick : null}
                />
            )}

            {/* チュートリアルモーダル */}
            {isModalOpen && (
                <TutorialModal
                    step={activeStep?.action}
                    onNext={handleModalCloseAction}
                />
            )}

            {/* マリガンモーダル */}
            {gameState.phase === GamePhase.MULLIGAN && (
                <MulliganModal
                    hand={humanPlayer.hand}
                    onConfirmMulligan={(selectedCardIds) => onConfirmMulligan(HUMAN_PLAYER_ID, selectedCardIds)}
                    cardDefs={cardDefs}
                    mulliganState={gameState.mulligan_state}
                    firstPlayer={gameState.first_player}
                />
            )}

            {/* NPCController - 表示要素なし、バックグラウンドで動作 */}
            <NPCController
                gameState={gameState}
                onPlayCard={effectiveOnPlayCard} // ラップされた方を使用
                onEndTurn={effectiveOnEndTurn} // ラップされた方を使用
                onProvideInput={effectiveOnProvideInput} // ラップされた方を使用
                onPerformMulligan={(selectedCardIds) => onConfirmMulligan(NPC_PLAYER_ID, selectedCardIds)}
                onRedevelopmentPlan={onRedevelopmentPlan}
            />
            
            {renderChoicePrompt()}

            {showSurrenderConfirm && (
                <div className={`choice-prompt-overlay ${isClosingPrompt ? 'closing' : ''}`} onClick={handleCancelSurrender}>
                    <div className={`choice-prompt ${isClosingPrompt ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <h3>{gameState?.isHypothesisMode ? getUILoc('MESSAGES.EXIT_EXPERIMENT_CONFIRM') : getUILoc('MESSAGES.SURRENDER_CONFIRM')}</h3>
                        <p>{gameState?.isHypothesisMode ? getUILoc('MESSAGES.EXIT_EXPERIMENT_WARNING') : getUILoc('MESSAGES.SURRENDER_WARNING')}</p>
                        <div className="choice-options">
                            <button
                                className="surrender-confirm-yes"
                                onClick={() => {
                                    onSurrender();
                                    setShowSurrenderConfirm(false);
                                }}
                            >
                                {getUILoc('BUTTONS.YES')}
                            </button>
                            <button onClick={handleCancelSurrender}>
                                {getUILoc('BUTTONS.NO')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 再開発計画モーダル */}
            {showRedevelopmentModal && (
                <div className={`choice-prompt-overlay ${isClosingPrompt ? 'closing' : ''}`} onClick={handleCancelRedevelopment}>
                    <div className={`choice-prompt ${isClosingPrompt ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
                        {redevelopmentConfirmCard ? (
                            <>
                                <h3>{getUILoc('MESSAGES.REDEVELOPMENT_CARD_CONFIRM').replace('{cardName}', getCardName(redevelopmentConfirmCard.id))}</h3>
                                <div className="choice-options">
                                    <button
                                        className="redevelopment-confirm-yes"
                                        onClick={handleRedevelopmentConfirm}
                                    >
                                        {getUILoc('BUTTONS.YES')}
                                    </button>
                                    <button onClick={() => { audioManager.playSE('cancel'); setRedevelopmentConfirmCard(null); }}>
                                        {getUILoc('BUTTONS.NO')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3>{getUILoc('MESSAGES.REDEVELOPMENT_CONFIRM')}</h3>
                                <p>{getUILoc('MESSAGES.REDEVELOPMENT_WARNING')}</p>
                                <div className="choice-options-scrollable">
                                    {humanPlayer.field.filter(c => c.card_type === 1).map(card => (
                                        <div key={card.instance_id} className="choice-card-container">
                                            <button className="choice-card-select" onClick={() => handleRedevelopmentCardSelect(card)}>
                                                <div className="choice-card-illustration">
                                                    <CardIllustration cardId={card.id} />
                                                </div>
                                                <div className="choice-card-info">
                                                    <span className="choice-card-name">{getCardName(card.id)}</span>
                                                    <span className="choice-card-type">({getUILoc('UI.CARD_TYPES.1') || '財'})</span>
                                                    {card.current_durability !== undefined && (
                                                        <span className="choice-card-durability">{getUILoc('LABELS.DURABILITY')}{card.current_durability}</span>
                                                    )}
                                                </div>
                                            </button>
                                            <button
                                                className="choice-card-detail"
                                                onClick={() => setSelectedCard(card)}
                                                title={getUILoc('BUTTONS.DETAIL')}
                                            >
                                                📋
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="choice-options" style={{ marginTop: '8px' }}>
                                    <button onClick={handleCancelRedevelopment}>
                                        {getUILoc('BUTTONS.CANCEL')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            
            {actionMenuCard && (
                <CardActionMenu
                    card={actionMenuCard}
                    player={humanPlayer}
                    gameState={gameState}
                    cardDefs={cardDefs}
                    onPlay={handlePlayFromMenu}
                    onClose={() => setActionMenuCard(null)}
                />
            )}
            
            {/* ゲームログオーバーレイ */}
            {showGameLog && (
                <>
                    {}
                    <GameLogOverlay
                        gameState={gameState}
                        cardDefs={cardDefs}
                        logEntries={enhancedLog.combinedLog} 
                        getFilteredEntries={enhancedLog.getFilteredEntries} 
                        onClose={() => setShowGameLog(false)}
                    />
                </>
            )}
            
            {/* ルールオーバーレイ */}
            {showRulesOverlay && (
                <RulesOverlay
                    isOpen={showRulesOverlay}
                    onClose={() => setShowRulesOverlay(false)}
                />
            )}
            
            {/* カード詳細オーバーレイ */}
            {selectedCard && (
                <CardDetail 
                    card={selectedCard} 
                    cardDefs={cardDefs}
                    onClose={() => setSelectedCard(null)} 
                />
            )}
            
            {/* 右上: ゲーム情報エリア */}
            <div className="game-info-area">
                <GameInfo 
                    gameState={gameState}
                    enhancedLog={enhancedLog}
                    onShowLog={() => setShowGameLog(true)}
                    onShowRules={() => setShowRulesOverlay(true)}
                />
            </div>
            
            {/* 左上: 相手（NPC）ステータス */}
            <div className={`opponent-stats-area ${current_turn === NPC_PLAYER_ID ? 'opponent-turn-indicator' : ''}`}>
                {current_turn === NPC_PLAYER_ID && (
                    <div className="turn-label opponent-turn-label">
                        ▶ {getUILoc('LABELS.OPPONENTS_TURN')}
                    </div>
                )}
                <PlayerStats 
                    player={npcPlayer} 
                    gameState={gameState} 
                    showSubInfo={isWidescreen}
                    onCardClick={handleCardClick}
                />
            </div>
            
            {/* 中央上: 相手（NPC）の場 */}
            <div className="opponent-field-area">
                <Field 
                    playerName={npcPlayer.name}
                    cards={npcPresentedCards}
                    fieldLimit={npcPlayer.field_limit}
                    onProvideInput={effectiveOnProvideInput} 
                    awaiting_input={awaiting_input}
                    onCardClick={handleCardClick}
                    onAnimationEnd={handleNpcAnimationEnd}
                />
            </div>
            
            {/* 中央下: 自分（人間）の場 */}
            <div className="player-field-area">
                <Field 
                    playerName={humanPlayer.name}
                    cards={humanPresentedCards}
                    fieldLimit={humanPlayer.field_limit}
                    onProvideInput={effectiveOnProvideInput} 
                    awaiting_input={awaiting_input}
                    onCardClick={handleCardClick}
                    onAnimationEnd={handleHumanAnimationEnd}
                />
            </div>
            
            {/* 右下: 自分（人間）の手札 */}
            <div id="player-hand-area" className="player-hand-area">
                <Hand 
                    player={humanPlayer} 
                    onPlayCard={effectiveOnPlayCard} 
                    onProvideInput={effectiveOnProvideInput} 
                    awaiting_input={awaiting_input}
                    onCardClick={handleCardClick}
                    gameState={gameState}
                />
                <button className="surrender-button" onClick={handleSurrenderClick}>
                    {gameState?.isHypothesisMode ? getUILoc('BUTTONS.END_EXPERIMENT') : getUILoc('BUTTONS.SURRENDER')}
                </button>
                {canUseRedevelopmentPlan() && (
                    <button className="redevelopment-button" onClick={handleRedevelopmentClick}>
                        {getUILoc('BUTTONS.REDEVELOPMENT')}
                    </button>
                )}
            </div>
            
            {/* 左下: 自分（人間）ステータス */}
            <div className={`player-stats-area ${current_turn === HUMAN_PLAYER_ID ? 'current-turn-indicator' : ''}`}>
                {current_turn === HUMAN_PLAYER_ID && (
                    <div className="turn-label player-turn-label">
                        ▶ {getUILoc('LABELS.YOUR_TURN')}
                    </div>
                )}
                <PlayerStats 
                    player={humanPlayer} 
                    gameState={gameState} 
                    onEndTurn={effectiveOnEndTurn}
                    showSubInfo={isWidescreen}
                    onCardClick={handleCardClick}
                />
            </div>

            {/* アニメーションロック中のオーバーレイ */}
            {gameState.isAnimationLocked && (
                <div className="animation-lock-overlay">
                    <div className="animation-lock-message">
                        <div>🎬 {getUILoc('MESSAGES.WAITING_FOR_ANIMATION')}</div>
                        <div style={{ fontSize: '0.8em', marginTop: '5px' }}>{getUILoc('MESSAGES.PLEASE_WAIT')}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Game;